import React from 'react';
import { ArrowLeft, Calendar, ExternalLink, Mic, Radio, Users, MessageSquare } from 'lucide-react';
import { speakingEngagements } from '../content/speaking';
const typeConfig = {
  talk: {
    icon: Mic,
    label: 'Talk',
    color: 'from-blue-500/20 to-cyan-500/20',
    badge: 'bg-blue-500/20 text-blue-300 border-blue-500/30'
  },
  podcast: {
    icon: Radio,
    label: 'Podcast',
    color: 'from-purple-500/20 to-pink-500/20',
    badge: 'bg-purple-500/20 text-purple-300 border-purple-500/30'
  },
  workshop: {
    icon: Users,
    label: 'Workshop',
    color: 'from-emerald-500/20 to-teal-500/20',
    badge: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'
  },
  panel: {
    icon: MessageSquare,
    label: 'Panel',
    color: 'from-orange-500/20 to-rose-500/20',
    badge: 'bg-orange-500/20 text-orange-300 border-orange-500/30'
  }
};
const isUpcoming = (dateStr: string) => {
  const date = new Date(dateStr);
  const now = new Date();
  return date > now;
};
interface SpeakingPostProps {
  slug: string;
  onBack: () => void;
}
export function SpeakingPost({
  slug,
  onBack
}: SpeakingPostProps) {
  const engagement = speakingEngagements.find(e => e.slug === slug);
  if (!engagement) {
    return <main className="bg-background text-foreground min-h-screen" data-id="element-201">
        <div className="container mx-auto px-6 md:px-12 py-24" data-id="element-202">
          <button onClick={onBack} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8" data-id="element-203">
            <ArrowLeft size={20} data-id="element-204" />
            Back
          </button>
          <h1 className="text-3xl font-bold" data-id="element-205">Speaking engagement not found</h1>
        </div>
      </main>;
  }
  const config = typeConfig[engagement.type];
  const Icon = config.icon;
  const upcoming = isUpcoming(engagement.date);
  return <main className="bg-background text-foreground min-h-screen" data-id="element-206">
      <div className="container mx-auto px-6 md:px-12 py-24 max-w-4xl" data-id="element-207">
        <button onClick={onBack} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-12 group" data-id="element-208">
          <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" data-id="element-209" />
          Back to all speaking
        </button>

        <article className="space-y-8" data-id="element-210">
          {/* Header */}
          <div className="space-y-6" data-id="element-211">
            <div className="flex flex-wrap items-center gap-3" data-id="element-212">
              <span className={`inline-flex items-center gap-2 text-sm px-3 py-1.5 rounded-lg border ${config.badge}`} data-id="element-213">
                <Icon size={16} data-id="element-214" />
                {config.label}
              </span>
              {upcoming && <span className="inline-block text-sm px-3 py-1.5 rounded-lg border bg-emerald-500/20 text-emerald-300 border-emerald-500/30" data-id="element-215">
                  Upcoming
                </span>}
            </div>

            <h1 className="text-4xl md:text-5xl font-bold tracking-tight leading-tight" data-id="element-216">
              {engagement.title}
            </h1>

            <div className="flex flex-wrap items-center gap-6 text-muted-foreground" data-id="element-217">
              <div className="flex items-center gap-2" data-id="element-218">
                <Calendar size={16} data-id="element-219" />
                <span className="font-mono text-sm" data-id="element-220">{engagement.date}</span>
              </div>
              <div className="text-sm font-medium" data-id="element-221">{engagement.event}</div>
            </div>

            {engagement.link && <a href={engagement.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors text-sm font-medium" data-id="element-222">
                View event details
                <ExternalLink size={16} data-id="element-223" />
              </a>}
          </div>

          {/* Divider */}
          <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent" data-id="element-224" />

          {/* Content */}
          <div className="prose prose-lg dark:prose-invert max-w-none" data-id="element-225">
            <p className="text-lg leading-relaxed text-muted-foreground" data-id="element-226">
              {engagement.description}
            </p>
          </div>

          {/* Background gradient */}
          <div className={`absolute inset-0 bg-gradient-to-br ${config.color} opacity-5 -z-10 rounded-3xl blur-3xl`} data-id="element-227" />
        </article>
      </div>
    </main>;
}